/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import Modal from 'react-modal'
import { withRouter } from 'react-router'
import cn from 'classnames'
import generateUuid from 'uuid/v4'
import { get } from 'lodash'
import { centsToDollars } from '../../utils/formatters'

import { getCartItems } from '../../selectors/cart'
import { getTotal, makeGetCartItems } from '../../selectors/cart'

import { toggleAppState } from '../../actions/appState'
import { startOfflineCardOrder, startOnlineCardOrder } from '../../actions/order'

import { formatPrice } from '../utils/formatters'
import PeripheralBridge from '../../utils/peripheralBridge'
import { isOfflineApproval } from '../../utils/offlineReplayStatuses'
import { generateURL } from '../../utils/richCheckout'
import { nextPosOrderNumber } from '../../utils/orderNumbers'

import { fakeCreditCardResponse, showFakeCreditCard } from '../../utils/fakeCreditCardResponse'

import {
  RICH_CHECKOUT_TEXT_PROMPT,
  SIGNATURE_THRESHOLD_AMOUNT,
  USE_RICH_CHECKOUT,
} from '../../constants'

import Button from '../components/Button'
import QRCode from '../../components/QRCode'
import BackButton from '../components/BackButton'
import { ReactComponent as IconCardReader } from '../assets/icons/card-reader.svg'
import { ReactComponent as IconCard } from '../assets/icons/card.svg'
import { ReactComponent as IconArrow } from '../assets/icons/arrow.svg'

import UserIdleMonitor from '../utils/UserIdleMonitor'

import VNPusher, {
  CHANNEL_PRIVATE_SOURCE_DEVICE_ID,
  PUSHER_EVENT_ORDER_TRANSFERRED,
  BIND_PAYMENT_INDEX } from '../../pusher'

// TODO:: move to the utils
const getDeviceId = () => localStorage.getItem('device_id')

export const Payment = ({
  appPaymentSubHeader,
  cart,
  deviceId,
  getNextOrderNumber,
  history,
  isOnlyCardPayment,
  kioskLineItems,
  onSuccess,
  onOfflineSuccess,
  toggleAppState,
  totalOrderPrice,
}) => {
  const [orderUuid] = useState(generateUuid())
  const [orderNumber] = useState(getNextOrderNumber())
  const [formattedTotal] = useState(centsToDollars(totalOrderPrice || 0))
  const [showPaymentCancelledByUserWarning, setShowPaymentCancelledByUserWarning] = useState(false)
  const qrCodeData = generateURL(orderUuid, orderNumber, deviceId, totalOrderPrice, cart)

  const getCardReaderReady = () => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (bridgeInstance) bridgeInstance.call('executeSaleRequest', { 'uuid': orderUuid, 'amount': formattedTotal })
  }

  const idleMonitor = new UserIdleMonitor(process.env.REACT_APP_DEV_IDLE_TIMEOUT || 30000)

  useEffect(() => {
    document.title = 'Order payment'

    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) return

    bridgeInstance.registerHandler('handleResult', (data) => {
      if (data.status === 'ACCEPT') {
        const dataWithUuid = { ...data, uuid: orderUuid, orderNumber }

        // passing in line items to avoid race condition of cart being cleared by the time we submit order
        isOfflineApproval(data) ? onOfflineSuccess(dataWithUuid, kioskLineItems) : onSuccess(dataWithUuid, kioskLineItems)

        redirectToNextStep()
      } else {
        setShowPaymentCancelledByUserWarning(true)
      }
    })

    getCardReaderReady()

    return () => { bridgeInstance.call('cancelTransactionAttempt') }
  }, [orderUuid, totalOrderPrice])

  useEffect(() => {

    if (!isOnlyCardPayment) {
      VNPusher.subscribe(CHANNEL_PRIVATE_SOURCE_DEVICE_ID)
      VNPusher.bind(BIND_PAYMENT_INDEX, CHANNEL_PRIVATE_SOURCE_DEVICE_ID, PUSHER_EVENT_ORDER_TRANSFERRED, (data) => {
        history.push('/kiosk/order/scanned')
      })
    }

    idleMonitor.addEventListeners()
    return () => {
      idleMonitor.removeEventListeners()
      VNPusher.unsubscribe(CHANNEL_PRIVATE_SOURCE_DEVICE_ID)
    };
  }, [])

  const redirectToNextStep = () => {
    if (totalOrderPrice >= SIGNATURE_THRESHOLD_AMOUNT) {
      history.push(`/kiosk/signature/${orderUuid}`)
    } else {
      history.push('/kiosk/order/receipt_selection')
    }
  }

  const onFakeCreditCardSwipe = () => {
    onSuccess({ ...fakeCreditCardResponse(orderUuid, formattedTotal), orderNumber }, kioskLineItems)
    redirectToNextStep()
  }

  const retryPaymentFlow = () => {
    getCardReaderReady()
    setShowPaymentCancelledByUserWarning(false)
  }

  const goBack = () => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (bridgeInstance) bridgeInstance.call('cancelTransactionAttempt')

    history.push('/kiosk/order/new')
  }

  const PaymentBackBtn = () => (
    <BackButton className="payment__back" withIcon={true} onClick={goBack}>
      {formatPrice(totalOrderPrice)}
    </BackButton>
  )

  return (
    <div className={cn("payment", { '-card-only': isOnlyCardPayment })}>
      {
        isOnlyCardPayment ? (
          <>
            <PaymentBackBtn />
            <div className="payment__header">Pay with card</div>
            <div className="payment__sub-header">All major credit cards accepted. Follow instructions on card reader.</div>
            <div className="payment__methods -top" />
            <div className="payment__card-wrapper">
              <IconCardReader className="payment__card-reader-icon" />
              <IconArrow className="payment__arrow-icon" />
              <IconCard className="payment__card-icon" />
            </div>
            <div className="payment__methods">
              Card Reader is Ready
            </div>
          </>
        ) : (
          <>
            <div className="payment__top-half">
              <PaymentBackBtn />
              <div className="payment__app-method">
                <div className="payment__header">Pay with the app</div>
                <div className="payment__sub-header">{appPaymentSubHeader}</div>
                <div className="payment__qr-code">
                  <QRCode data={qrCodeData} />
                </div>
              </div>
            </div>
            <div className="payment__card-method">
              <div className="payment__header">Pay with card</div>
              <div className="payment__sub-header">All major credit cards accepted. Follow instructions on card reader.</div>
              <div className="payment__card">
                <IconCard className="payment__card-icon" />
              </div>
            </div>
          </>
        )
      }

      { showFakeCreditCard() && (
        <div className="fake-credit-card">
          <Button className="btn -primary" onClick={onFakeCreditCardSwipe} title="Pretend Credit Card was swiped" />
        </div>
      )}

      <Modal
        className="modal"
        isOpen={showPaymentCancelledByUserWarning}
        overlayClassName="overlay"
      >
        <div className="modal__copy">Would you like to try another card?</div>
        <div className="modal__actions">
          <Button
            className="btn -outline"
            onClick={() => history.push('/kiosk/attract_loop')}
            title="No"
          />
          <Button className="btn -primary" onClick={retryPaymentFlow} title="Yes" />
        </div>
      </Modal>
    </div>
  )
}

const mapStateToProps = state => ({
  appPaymentSubHeader: RICH_CHECKOUT_TEXT_PROMPT,
  cart: getCartItems(state),
  deviceId: get(state, 'config.uuid', getDeviceId()), // TODO: This should be a temporary change.
  getNextOrderNumber: nextPosOrderNumber,
  isOnlyCardPayment: USE_RICH_CHECKOUT === 'false',
  kioskLineItems: makeGetCartItems()(state),
  totalOrderPrice: getTotal(state),
})

const mapDispatchToProps = (dispatch) => ({
  onOfflineSuccess: (cardReaderData, lineItems) => {
    dispatch(startOfflineCardOrder({ cardReaderData, lineItems, isKiosk: true }))
  },
  onSuccess: (cardReaderData, lineItems) => {
    dispatch(startOnlineCardOrder({ cardReaderData, lineItems, isKiosk: true }))
  },
  toggleAppState: (data) => {
    dispatch(toggleAppState({ data }))
  },
})

Payment.defaultProps = {
  cart: [],
  kioskLineItems: [],
  totalOrderPrice: 0,
}

Payment.propTypes = {
  appPaymentSubHeader: PropTypes.string,
  cart: PropTypes.array,
  deviceId: PropTypes.string,
  getNextOrderNumber: PropTypes.func,
  isOnlyCardPayment: PropTypes.bool,
  kioskLineItems: PropTypes.array,
  onOfflineSuccess: PropTypes.func,
  onSuccess: PropTypes.func,
  toggleAppState: PropTypes.func,
  totalOrderPrice: PropTypes.number,
}

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withRouter,
)(Payment)
